import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  catchError,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  Observable,
  of,
  take,
  tap,
} from 'rxjs';

import {
  BasePermission,
  PermissionGrants,
  RolePermission,
  UserRole,
} from '@shared/interfaces/permissions.interface';
import {
  setEntityRole,
  setRole,
  setRolePermissions,
  setRolePermissionsLoading,
} from '@shared/store/actions/role.actions';
import {
  getEntityRole,
  getEntityRoles,
  getRole,
  getRolePermission,
  loadingRolePermissions,
} from '@shared/store/selectors/role.selectors';
import { UserService } from '@user/user.service';
import { AdminService } from '@admin/admin.service';
import { selectUser } from '@shared/store/selectors/user.selectors';
import { User } from '@iris-sites/interfaces/sites.interfaces';
import { checkPermissions } from '@shared/utils/permissions.util';

@Injectable({
  providedIn: 'root',
})
export class PermissionsService {
  private readonly _adminService = inject(AdminService);
  private readonly _userService = inject(UserService);
  private readonly _store = inject(Store);

  role$ = this._store.select(getRole);
  entityRoles$ = this._store.select(getEntityRoles);

  currentUser$ = this._store.select(selectUser);

  isInternal$ = this._userService.user$.pipe(
    filter((user: User | undefined): user is User => !!user),
    map((user) => !!user.is_internal),
  );

  constructor() {
    this._userService.user$
      .pipe(
        distinctUntilChanged(),
        filter((user) => !!user),
      )
      .subscribe((user) => {
        this.setRole({
          id: user!.role?.id,
          name: user!.role?.name,
        });
      });
  }

  /**
   * Set the user's role at the organization level in the _store
   */
  public setRole(role: UserRole): void {
    this._store.dispatch(setRole({ role }));
  }

  /**
   * Set the user's role for a Entity in the _store
   */
  public setEntityRole(entityId: string, role: UserRole): void {
    this._store.dispatch(setEntityRole({ entityId, role }));
  }

  /**
   * Get the user's role for a study from the _store
   */
  public getEntityRole(entityId: string): Observable<UserRole> {
    return this._store.select(getEntityRole(entityId));
  }

  /**
   * Get the user's role from the _store as a one-time observable
   */
  public getRole(): Observable<UserRole> {
    return this.role$.pipe(take(1));
  }

  /**
   * Checks if the user has the required role
   */
  public hasRole(role: string): Observable<boolean> {
    return this.getRole().pipe(
      map((userRole) => userRole?.name === role || false),
    );
  }

  /**
   * Checks if the user has the required permissions and grants.
   * This is for the user's role, not an entity.
   * @param permissions - The permissions to check
   * @param grants - The grants to check
   * @deprecated Use observeUserPermissions instead
   */
  public hasUserPermission(
    permissions: BasePermission[],
    grants: PermissionGrants[],
  ): Observable<boolean> {
    return this.role$.pipe(
      mergeMap((userRole) =>
        this.hasPermission(userRole?.id, permissions, grants),
      ),
    );
  }

  /**
   * Observes the user's permissions and grants.
   * @param permissions - The permissions to check
   * @param grants - The grants to check
   */
  public observeUserPermissions(
    permissions: BasePermission[],
    grants: PermissionGrants[],
  ): Observable<boolean> {
    return this.role$.pipe(
      filter((userRole) => !!userRole),
      mergeMap((userRole) =>
        this.getRolePermissions(userRole.id).pipe(
          map((permissions) => [userRole, permissions]),
        ),
      ),
      filter(([_, permissions]) => !!permissions),
      mergeMap(([userRole]) =>
        this.hasPermission(userRole.id, permissions, grants),
      ),
    );
  }

  /**
   * Checks if the user has the required permissions and grants for an entity
   */
  public observeEntityPermissions(
    entityId: string,
    permissions: BasePermission[],
    grants: PermissionGrants[],
  ): Observable<boolean> {
    return combineLatest([
      this.role$,
      this._store.select(getEntityRole(entityId)),
      this.isInternal$,
    ]).pipe(
      mergeMap(([userRole, entityRole, internal]) => {
        if (!userRole) {
          console.warn('User role not found');
          return of(false);
        }

        if (internal) {
          return combineLatest([
            this.hasPermission(userRole?.id, permissions, grants),
            this.hasPermission(entityRole?.id, permissions, grants),
          ]).pipe(map((result) => result.some((r) => r)));
        }
        return this.hasPermission(entityRole.id, permissions, grants);
      }),
      catchError(() => of(false)), // Handle errors from mergeMap
    );
  }

  public setEntityRolesAndPermission(entityId: string, role: any) {
    this.setEntityRole(entityId, role);
    if (role) {
      const roleId = role.id;
      const permissions = role.permissions;
      this._store.dispatch(setRolePermissions({ roleId, permissions }));
    }
  }

  /**
   * Checks if the user has the required permissions and grants
   */
  private hasPermission(
    userRole: string | undefined,
    permissions: BasePermission[],
    grants: PermissionGrants[],
  ): Observable<boolean> {
    if (!userRole) {
      return of(false); // No role found for the user
    }

    return this.getRolePermissions(userRole).pipe(
      map((userPermissions) => {
        /*
         * In the context of javascript or typescript,
         * An empty array is a truthy value, so it will not be equal to false.
         * The following if condition will never be true. I.e why we need to check for the length of the array.
         * */
        if (!userPermissions?.length) {
          return false; // No permissions found for the user
        }

        return checkPermissions(userPermissions, permissions, grants);
      }),
      catchError((error) => {
        console.error('Error fetching role permissions:', error);
        return of(false); // Return false in case of an error
      }),
    );
  }

  private getRolePermissions(roleId: string) {
    return combineLatest([
      this._store.select(getRolePermission(roleId)),
      this._store.select(loadingRolePermissions(roleId)),
    ]).pipe(
      take(1),
      mergeMap(([permissions, loading]) => {
        if (permissions || loading) {
          return this._store.select(getRolePermission(roleId));
        }

        return this.loadRolePermissions(roleId);
      }),
    );
  }

  private loadRolePermissions(roleId: string): Observable<RolePermission[]> {
    this._store.dispatch(setRolePermissionsLoading({ roleId, loading: true }));

    this._adminService
      .getUserRolePermissionList()
      .pipe(
        tap((permissions) => {
          this._store.dispatch(setRolePermissions({ roleId, permissions }));
        }),
      )
      .subscribe();

    return this._store.select(getRolePermission(roleId));
  }
}
